import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { LayerStack } from "./layers.js";

const svg = (path: ReactNode, viewBox = "0 0 20 20") => (
  <svg viewBox={viewBox} aria-hidden="true" focusable="false" className="bp-icon">
    {path}
  </svg>
);

export const Icon = {
  warning: svg(<path fill="currentColor" d="M10 2.2c.5 0 .9.3 1.2.7l7 12.4c.5.9-.1 2-1.2 2H3c-1 0-1.7-1.1-1.2-2l7-12.4c.3-.4.7-.7 1.2-.7Zm0 11.3a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2Zm0-7a1 1 0 0 0-1 1.1l.3 4.4a.7.7 0 0 0 1.4 0l.3-4.4a1 1 0 0 0-1-1.1Z" />),
  clock: svg(<g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="10" cy="10" r="7" /><path d="M10 6.5V10l2.5 1.5" /></g>),
  lock: svg(<g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><rect x="4.5" y="9" width="11" height="8" rx="2" /><path strokeLinecap="round" d="M7 9V6.5a3 3 0 0 1 6 0V9" /></g>),
  check: svg(<path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="m4.5 10.5 3.5 3.5 7.5-8" />),
  cloud: svg(<path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" d="M6 15.5h8.5a3.5 3.5 0 0 0 .4-7 5 5 0 0 0-9.6 1.3A2.9 2.9 0 0 0 6 15.5Z" />),
  sync: svg(<path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M15.5 8A6 6 0 0 0 4.6 7M4.5 12a6 6 0 0 0 10.9 1M4 3.5V7h3.5M16 16.5V13h-3.5" />),
  more: svg(<g fill="currentColor"><circle cx="4.5" cy="10" r="1.6" /><circle cx="10" cy="10" r="1.6" /><circle cx="15.5" cy="10" r="1.6" /></g>),
  plus: svg(<path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M10 4v12M4 10h12" />),
  close: svg(<path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="m5.5 5.5 9 9m0-9-9 9" />),
  undo: svg(<path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M7.5 5 4 8.5 7.5 12M4.5 8.5h7a4.5 4.5 0 0 1 0 9H9" />),
  redo: svg(<path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M12.5 5 16 8.5 12.5 12m3-3.5h-7a4.5 4.5 0 0 0 0 9H11" />),
  eye: svg(<g fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M1.8 10S5 4.5 10 4.5 18.2 10 18.2 10 15 15.5 10 15.5 1.8 10 1.8 10Z" /><circle cx="10" cy="10" r="2.6" /></g>),
  print: svg(<g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M5.5 7.5V3h9v4.5M5.5 14H3.5v-5a1.5 1.5 0 0 1 1.5-1.5h10A1.5 1.5 0 0 1 16.5 9v5h-2" /><path d="M5.5 11.5h9V17h-9z" /></g>),
  chart: svg(<g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 16V11M8 16V6M12 16V9M16 16V4" /></g>),
  table: svg(<g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><rect x="3" y="3.5" width="14" height="13" rx="1.5" /><path d="M3 8h14M3 12.3h14M8 8v8.5" /></g>),
  send: svg(<path fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" d="M17 3 8.5 11.5M17 3l-5.5 14-3-5.5L3 8.5 17 3Z" />),
  keyboard: svg(<g fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="5" width="16" height="10" rx="2" /><path strokeLinecap="round" d="M5 8.5h.01M8 8.5h.01M11 8.5h.01M14 8.5h.01M6 12h8" /></g>),
  chevronLeft: svg(<path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12.5 4 6.5 10l6 6" />),
  chevron: svg(<path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="m7.5 4 6 6-6 6" />),
  chevronDown: svg(<path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="m4 7.5 6 6 6-6" />),
  trash: svg(<path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M3.5 5.5h13M8 5.5V4h4v1.5M5 5.5l.8 10.6a1 1 0 0 0 1 .9h6.4a1 1 0 0 0 1-.9L15 5.5M8.5 8.5v5.5M11.5 8.5v5.5" />),
  dot: svg(<circle cx="10" cy="10" r="4" fill="currentColor" />),
  info: svg(<g fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="10" cy="10" r="7.2" /><path d="M10 9v4.5M10 6.6v.1" /></g>),
  people: svg(<path fill="currentColor" d="M5 4.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm0 1.4a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2ZM15 4.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm0 1.4a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2ZM10 10.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm0 1.4a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2Z" />),
  calendar: svg(<g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><rect x="3" y="4.5" width="14" height="12" rx="2" /><path strokeLinecap="round" d="M3 8.5h14M7 2.5v3.5M13 2.5v3.5" /></g>),
  grip: svg(<g fill="currentColor"><circle cx="7.5" cy="5" r="1.3" /><circle cx="12.5" cy="5" r="1.3" /><circle cx="7.5" cy="10" r="1.3" /><circle cx="12.5" cy="10" r="1.3" /><circle cx="7.5" cy="15" r="1.3" /><circle cx="12.5" cy="15" r="1.3" /></g>),
  edit: svg(<path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" d="M12.5 4.5 15.5 7.5 7.5 15.5H4.5v-3l8-8ZM11 6l3 3" />),
  diamond: svg(<path fill="currentColor" d="M10 3.5 16.5 10 10 16.5 3.5 10Z" />),
};

/** A modal sheet on the native `<dialog>`, which brings focus trapping and Escape for free. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  wide,
  labelledBy,
  className,
  footer,
  head,
  layers,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
  labelledBy?: string;
  className?: string;
  footer?: ReactNode;
  /** Replaces the title heading (give `labelledBy` then). */
  head?: ReactNode;
  /** Surfaces open inside it: Escape closes the newest of them first, the sheet only when none is open. */
  layers?: LayerStack;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const downOutside = useRef(false);
  const outside = (e: { target: EventTarget; clientX: number; clientY: number }) => {
    const d = ref.current;
    if (!d || e.target !== d) return false;
    const r = d.getBoundingClientRect();
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
  };
  const id = useRef(`bp-dialog-${Math.random().toString(36).slice(2, 8)}`).current;
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      opener.current = document.activeElement;
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
      // A field's autoFocus runs while the dialog is still hidden, so it is marked and focused here instead.
      dialog.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    } else if (!open && dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    }
  }, [open]);
  if (!open) return <dialog ref={ref} className="bp-dialog" hidden />;
  return (
    <dialog
      ref={ref}
      className={"bp-dialog" + (wide ? " bp-dialog--wide" : "") + (className ? " " + className : "")}
      aria-labelledby={labelledBy ?? id}
      onCancel={(e) => {
        e.preventDefault();
        if (!layers?.pop()) onClose();
      }}
      // A click on the backdrop closes, when it also started there: a text selection dragged out of the
      // sheet ends outside it and must not close it.
      onPointerDown={(e) => {
        downOutside.current = outside(e);
      }}
      onClick={(e) => {
        if (downOutside.current && outside(e)) onClose();
        downOutside.current = false;
      }}
      onKeyDownCapture={layers?.onKeyDownCapture}
      onKeyDown={(e) => {
        e.stopPropagation();
        // Handled here too, so Escape closes where the native `cancel` doesn't come (no showModal).
        if (e.key === "Escape" && !e.defaultPrevented) {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className="bp-dialog-head">
        {head ?? <h2 id={id}>{title}</h2>}
        <button type="button" className="bp-icon-btn" onClick={onClose} aria-label="Close">
          {Icon.close}
        </button>
      </div>
      <div className="bp-dialog-body">{children}</div>
      {footer ? <div className="bp-dialog-foot">{footer}</div> : null}
    </dialog>
  );
}

/** One polite live region for what changes off-screen (grid doc §6). */
export function useAnnouncer(): [string, (text: string) => void] {
  const [text, setText] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const announce = useCallback((next: string) => {
    setText("");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setText(next), 30);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [text, announce];
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} className="bp-switch" disabled={disabled} onClick={() => onChange(!checked)}>
      <span className="bp-switch-track" aria-hidden="true">
        <span className="bp-switch-thumb" />
      </span>
      <span className="bp-switch-text" aria-hidden="true">
        {checked ? "On" : "Off"}
      </span>
    </button>
  );
}

export function useStored(key: string, fallback: string): [string, (v: string) => void] {
  const [value, setValue] = useState(() => {
    try {
      return window.localStorage.getItem(key) ?? fallback;
    } catch {
      return fallback;
    }
  });
  const set = useCallback(
    (v: string) => {
      setValue(v);
      try {
        window.localStorage.setItem(key, v);
      } catch {
        /* per-viewer convenience only */
      }
    },
    [key],
  );
  return [value, set];
}

export const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const MOD = isMac ? "⌘" : "Ctrl+";
export const ALT = isMac ? "⌥" : "Alt+";
